/**
 * Utility functions for working with localStorage with expiration support
 */

import { ORDER_SESSION_STORAGE_MAX_AGE_HOURS } from "./order-freshness";

/**
 * Set an item in localStorage with an expiration time
 * @param key The key to store the value under
 * @param value The value to store
 * @param expirationHours Number of hours until the item expires (default: 8)
 */
export const setWithExpiry = (key: string, value: string, expirationHours: number = 8): void => {
  try {
    const now = new Date();
    const item = {
      value: value,
      expiry: now.getTime() + expirationHours * 60 * 60 * 1000, // Convert hours to milliseconds
    };
    localStorage.setItem(key, JSON.stringify(item));
  } catch (error) {
    console.error(`Error saving ${key} to localStorage:`, error);
  }
};

/**
 * Get an item from localStorage, checking if it has expired
 * @param key The key to retrieve
 * @returns The stored value if it exists and hasn't expired, null otherwise
 */
export const getWithExpiry = (key: string): string | null => {
  try {
    const itemStr = localStorage.getItem(key);
    
    // Return null if item doesn't exist
    if (!itemStr) {
      return null;
    }
    
    const item = JSON.parse(itemStr);
    const now = new Date();
    
    // Compare the expiry time with the current time
    if (now.getTime() > item.expiry) {
      // Item has expired, remove it from localStorage
      localStorage.removeItem(key);
      return null;
    }
    
    return item.value;
  } catch (error) {
    console.error(`Error retrieving ${key} from localStorage:`, error);
    return null;
  }
};

/**
 * Remove an item from localStorage
 * @param key The key to remove
 */
export const removeItem = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Error removing ${key} from localStorage:`, error);
  }
};

/**
 * Save order data to localStorage with expiry
 * @param orderId The order ID
 * @param guestToken The guest token
 * @param expirationHours Number of hours until the data expires. Defaults to
 *        ORDER_SESSION_STORAGE_MAX_AGE_HOURS — derived from CART_MAX_AGE_DAYS,
 *        never a literal.
 *
 * This was 8h, then a hard-coded 48h to match the `orderId` / `guest_token`
 * cookies. Both were literals, so when the cart's age ceiling was raised to 5
 * days the backup still evaporated on day two and the policy change did nothing.
 * Deriving the lifetime means the backup always outlives the cart it backs up;
 * genuine staleness is handled by the age check in order-freshness.ts on load.
 */
export const saveOrderData = (
  orderId: string,
  guestToken: string,
  expirationHours: number = ORDER_SESSION_STORAGE_MAX_AGE_HOURS,
): void => {
  setWithExpiry('savedOrderId', orderId, expirationHours);
  setWithExpiry('savedGuestToken', guestToken, expirationHours);
};

/**
 * Get order data from localStorage if not expired
 * @returns An object with orderId and guestToken if they exist and haven't expired, null otherwise
 */
export const getOrderData = (): { orderId: string; guestToken: string } | null => {
  const orderId = getWithExpiry('savedOrderId');
  const guestToken = getWithExpiry('savedGuestToken');
  
  if (orderId && guestToken) {
    return { orderId, guestToken };
  }
  
  return null;
};

/**
 * Clear order data from localStorage
 */
export const clearOrderData = (): void => {
  removeItem('savedOrderId');
  removeItem('savedGuestToken');
};
